import StripeElementsProvider from '@liquidweb-dev/undercurrent/stripe';
import { type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import appConfigSelectors from 'modules/appConfig/selectors';
import getStripePromise from 'modules/stripe/getStripePromise';
import { useDialogMakePaymentContext } from './dialogMakePaymentContext';

/**
 * Stripe elements for the whole dialog, so the step that collects a card and the
 * step that submits it are both inside them - `useStripe` and `useElements` only
 * answer to what they wrap.
 */
const StripeProvider = ({ children }: { children: ReactNode }) => {
	const publishableKey = useSelector(
		appConfigSelectors.getStripePublishableKey,
	);
	const stripePromise = getStripePromise(publishableKey);

	const { formValues } = useDialogMakePaymentContext();

	// An environment with no publishable key has no Stripe at all, and
	// `DialogMakePayment` keeps its card entry from being offered - so nothing
	// below reaches for Elements, and the rest of the dialog still pays with what
	// the account has on file.
	if (!stripePromise) {
		return <>{children}</>;
	}

	/**
	 * In cents, the payment amount. Is a minimum of 50 cents since Stripe
	 * payment elements won't render for amount less than that.
	 */
	const stripeAmount = Math.max(
		Math.round(Number(formValues.amount) * 100),
		50,
	);

	return (
		<StripeElementsProvider
			stripe={stripePromise}
			options={{
				mode: 'payment',
				amount: stripeAmount,
				currency: 'usd',
				allowedPaymentMethodTypes: ['card'],
				// the intent `beginPayment` creates is authorized here and captured
				// later, on the confirmation step - Stripe refuses to confirm an
				// intent whose capture method these options do not agree with
				captureMethod: 'manual',
				// saving the card for later has `beginPayment` take a mandate for
				// future charges, which these options have to agree with as well
				setupFutureUsage: formValues.savePaymentMethod ? 'off_session' : null,
			}}
		>
			{children}
		</StripeElementsProvider>
	);
};

export default StripeProvider;
